import axios from 'axios';
import { toast } from 'react-toastify';

const AUTH_TOKEN_KEY = 'authToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const TOKEN_EXPIRATION_KEY = 'tokenExpiration';
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 segundo

class AuthService {
    constructor() {
        this.axiosInstance = axios.create({
            baseURL: process.env.REACT_APP_API_BASE_URL,
            withCredentials: true,
            timeout: 10000, // 10 segundos
        });

        this.setupInterceptors();
    }

    setupInterceptors() {
        // Interceptor para agregar el token a las peticiones
        this.axiosInstance.interceptors.request.use(
            (config) => {
                const token = this.getToken();
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Interceptor para manejar respuestas y refrescar token
        this.axiosInstance.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                // Si el error es 401 y no es una petición de refresh
                if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url.includes('refresh')) {
                    originalRequest._retry = true;

                    try {
                        const newToken = await this.refreshToken();
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        return this.axiosInstance(originalRequest);
                    } catch (refreshError) {
                        this.logout();
                        return Promise.reject(refreshError);
                    }
                }

                return Promise.reject(error);
            }
        );
    }

    async login(identificationNumber, username, password) {
        try {
            const response = await this.axiosInstance.post(process.env.REACT_APP_API_LOGIN_ROUTE, {
                identificationNumber,
                username,
                password
            });

            if (response.data.token) {
                this.setToken(response.data.token);
                if (response.data.refreshToken) {
                    this.setRefreshToken(response.data.refreshToken);
                }
                if (response.data.expiresIn) {
                    this.setTokenExpiration(response.data.expiresIn);
                }
            }

            return response.data;
        } catch (error) {
            this.handleAuthError(error);
            throw error;
        }
    }

    async refreshToken() {
        const refreshToken = this.getRefreshToken();
        if (!refreshToken) {
            throw new Error('No refresh token available');
        }

        try {
            const response = await this.axiosInstance.post(process.env.REACT_APP_API_REFRESH_TOKEN_ROUTE, {
                refreshToken
            });

            if (response.data.token) {
                this.setToken(response.data.token);
                if (response.data.refreshToken) {
                    this.setRefreshToken(response.data.refreshToken);
                }
                if (response.data.expiresIn) {
                    this.setTokenExpiration(response.data.expiresIn);
                }
            }

            return response.data.token;
        } catch (error) {
            this.logout();
            throw error;
        }
    }

    async logout() {
        try {
            await this.axiosInstance.post(process.env.REACT_APP_API_LOGOUT_ROUTE);
        } catch (error) {
            console.error('Error during logout:', error);
        } finally {
            this.clearAuthData();
        }
    }

    setToken(token) {
        localStorage.setItem(AUTH_TOKEN_KEY, token);
    }

    getToken() {
        return localStorage.getItem(AUTH_TOKEN_KEY);
    }

    setRefreshToken(token) {
        localStorage.setItem(REFRESH_TOKEN_KEY, token);
    }

    getRefreshToken() {
        return localStorage.getItem(REFRESH_TOKEN_KEY);
    }

    setTokenExpiration(expiresIn) {
        const expirationTime = Date.now() + expiresIn * 1000;
        localStorage.setItem(TOKEN_EXPIRATION_KEY, expirationTime.toString());
    }

    isTokenExpired() {
        const expirationTime = localStorage.getItem(TOKEN_EXPIRATION_KEY);
        if (!expirationTime) return true;
        return Date.now() > parseInt(expirationTime);
    }

    clearAuthData() {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(TOKEN_EXPIRATION_KEY);
    }

    handleAuthError(error) {
        if (error.response) {
            switch (error.response.status) {
                case 401:
                    toast.error('Credenciales inválidas');
                    break;
                case 403:
                    toast.error('No tiene permisos para realizar esta acción');
                    break;
                case 429:
                    toast.error('Demasiados intentos. Por favor, espere un momento');
                    break;
                default:
                    toast.error('Error de autenticación');
            }
        } else if (error.request) {
            toast.error('Error de conexión. Por favor, verifique su conexión a internet');
        } else {
            toast.error('Error inesperado');
        }
    }

    isAuthenticated() {
        const token = this.getToken();
        return !!token && !this.isTokenExpired();
    }
}

export const authService = new AuthService(); 